import React, {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useGetHeat, useSaveHeat} from "./heatHooks";
import {AgeCategory, HeatEliminationType, type HeatForm, HeatType, Sex} from '@iziping/common';
import styles from "@/styles/form.module.css";
import {useTranslation} from "react-i18next";
import { useNotification } from '@iziping/ui';
import {useForm} from "react-hook-form";
import type {ApiError} from '@iziping/common';
import { useTournamentStore } from '../../service/useTournamentStore';

const HeatPage: React.FC = () => {
    const {id} = useParams();
    const heatId = id ? parseInt(id, 10) : undefined;
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {notify} = useNotification();
    const {data: heat, isLoading, isError} = useGetHeat(heatId);
    const {selectedTournament} = useTournamentStore();

    const {register, handleSubmit, reset, formState: {errors}} = useForm<HeatForm>({
        defaultValues: {
            name: '',
            type: HeatType.SIMPLE,
            pointsLimit: null,
            tournamentId: selectedTournament?.id || 0,
            eliminationType: HeatEliminationType.POULE,
            ageCategory: AgeCategory.SENIOR,
            sexRestriction: null,
            playersLimit: null
        },
    });

    useEffect(() => {
        if (heat) {
            reset({
                name: heat.name,
                type: heat.type,
                pointsLimit: heat.pointsLimit,
                tournamentId: selectedTournament?.id || 0,
                eliminationType: heat.eliminationType,
                ageCategory: heat.ageCategory,
                sexRestriction: heat.sexRestriction,
                playersLimit: heat.playersLimit
            });
        } else reset({
            name: '',
            type: HeatType.SIMPLE,
            pointsLimit: null,
            tournamentId: selectedTournament?.id || 0,
            eliminationType: HeatEliminationType.POULE,
            ageCategory: null,
            sexRestriction: null,
            playersLimit: null
        });
    }, [heat, reset]);

    const saveMutation = useSaveHeat();

    const onSubmit = async (data: HeatForm) => {
        try {
            const payload = heat ? {id: heat.id, data} : {data};
            await saveMutation.mutateAsync(payload);
            notify(t('heat.created.success'), 'success');
            navigate(`/tournaments/${selectedTournament?.id}`);
        } catch (err: unknown) {
            const apiError = err as ApiError;
            notify(apiError?.error || 'Erreur inconnue', 'error');
        }
    };

    if (isLoading) return <div>Loading...</div>;

    if (isError) return <div>Error fetching data</div>;

    return (
        <div>
            <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
                <h2>{`${heatId ? t('heat.editing') : t('heat.creating')} ${selectedTournament?.name}`}</h2>
                <div className={styles.formGroup}>
                    <label htmlFor="name">{t('heat.name')}</label>
                    <input
                        id="name"
                        className={styles.input}
                        {...register('name', {required: t('heat.name.required')})}
                    />
                    {errors.name && <span className={styles.error}>{errors.name.message}</span>}
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="type">{t('heat.type')}</label>
                    <select id="type" className={styles.input} {...register('type')}>
                        {Object.values(HeatType).map(s => (
                            <option key={s} value={s}>{t(`HEATTYPE.${s}`)}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="eliminationType">{t('heat.eliminationType')}</label>
                    <select id="eliminationType" className={styles.input} {...register('eliminationType')}>
                        {Object.values(HeatEliminationType).map(s => (
                            <option key={s} value={s}>{t(`HEATELIMINATIONTYPE.${s}`)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="points">{t('heat.points.limit')}</label>
                    <input id="points" type="number" className={styles.input} {...register('pointsLimit')} />
                </div>
                <div className={styles.formGroup}>
                    <label htmlFor="playersLimit">{t('heat.players.limit')}</label>
                    <input id="playersLimit" type="number" className={styles.input} {...register('playersLimit')} />
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="age">{t('heat.age.limit')}</label>
                    <select id="age" className={styles.input}
                            {...register("ageCategory", {
                                setValueAs: (value) =>
                                    value === "" ? null : (value as AgeCategory),
                            })}
                    >
                        <option value=""></option>
                        {Object.values(AgeCategory).map(a => (
                            <option key={a} value={a}>{t(`AGECATEGORY.${a}`)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="sex">{t('heat.sex.limit')}</label>
                    <select id="sex" className={styles.input}
                            {...register("sexRestriction", {
                                setValueAs: (value) =>
                                    value === "" ? null : (value as Sex),
                            })}
                    >
                        <option value=""></option>
                        {Object.values(Sex).map(s => (
                            <option key={s} value={s}>{t(`SEX.${s}`)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.actionsContainer}>
                    <button type="button" className={styles.deleteButton}
                            onClick={() => navigate(`/tournaments/${selectedTournament?.id}`)}>
                        {t('heat.cancel.button')}
                    </button>

                    <button type="submit">
                        {heat ? t('heat.update.button') : t('heat.create.button')}
                    </button>
                </div>

            </form>
        </div>


    );
};

export default HeatPage;